/**
 * Game page — active game arena.
 * Handles the full game lifecycle: match found -> game play -> result.
 */

import { useParams, useSearchParams } from "react-router-dom";
import { useGame, type GameInfo } from "@/hooks/useGame";
import { GameArena } from "@/components/GameArena";
import { Loader2, AlertTriangle, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { GAME_TICTACTOE } from "@/lib/gameKinds";

export function GamePage() {
  const { gameId } = useParams<{ gameId: string }>();
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get("teamId");

  // Build game info from URL params
  // In production, this would come from the match found event
  const gameInfo: GameInfo | null = gameId
    ? {
        gameId,
        gameType: GAME_TICTACTOE,
        teamA: teamId ?? "unknown",
        teamAName: "Your Team",
        teamB: "opponent",
        teamBName: "Opponent",
      }
    : null;

  const { gameState, makeMove, winningLine, error } = useGame(gameInfo, teamId);

  if (!gameId) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-10 w-10 text-yellow-500" />
          <p className="mt-3 text-muted-foreground">No game ID provided.</p>
          <Link to="/" className="mt-4 inline-block text-sm text-primary hover:underline">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (!gameState) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
          <p className="mt-4 text-muted-foreground">Loading game...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Back button */}
      <div className="mx-auto max-w-2xl px-4 pt-4">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Link>
      </div>

      {/* Game arena */}
      <GameArena
        gameState={gameState}
        winningLine={winningLine}
        onMakeMove={makeMove}
      />

      {/* Error toast */}
      {error && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          {error}
        </div>
      )}
    </div>
  );
}